import {useFlux, useFluxListener} from '@nlabs/arkhamjs-utils-react';
import {Link, Navigate, Route, Routes, useLocation, useNavigate} from '@nlabs/gothamjs/router';
import {useCallback, useEffect, useMemo, useState} from 'react';

import {AUTH_EVENTS, createAuthActions} from '../../state/authActions.js';
import {AccountPage} from '../AccountPage/AccountPage.js';
import {App} from '../App/App.js';
import {AuthPage} from '../AuthPage/AuthPage.js';
import {FeaturesPage} from '../FeaturesPage/FeaturesPage.js';
import {LegalPage} from '../LegalPage/LegalPage.js';
import {MarketingHome} from '../MarketingHome/MarketingHome.js';
import {PricingPage} from '../PricingPage/PricingPage.js';
import {PublicShell} from '../PublicShell/PublicShell.js';

import type {ReactElement} from 'react';
import type {AuthEvent, AuthMode, AuthUser} from '../../state/authActions.js';

const authModes: readonly string[] = ['sign-in', 'sign-up', 'verify-email', 'resend-verification', 'forgot-password', 'reset-password', 'sign-out'];
const workspaceRoutes = ['projects', 'videos', 'studio', 'characters', 'assets', 'billing'];

export const SiteApp = (): ReactElement => {
  const flux = useFlux();
  const auth = useMemo(() => createAuthActions(flux), [flux]);
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [ready, setReady] = useState(false);
  const [sessionError, setSessionError] = useState(false);
  const route = location.pathname.slice(1);
  const params = new URLSearchParams(location.search);
  useFluxListener(AUTH_EVENTS.CHANGED, useCallback((event?: AuthEvent) => {
    setUser(flux.getState('auth.user', null) as AuthUser | null);
    setReady(true);
    setSessionError(false);
    if(['sign-in', 'sign-out', 'reset-password'].includes(event?.operation || '')) {
      void flux.setState('workspace.data', undefined);
      void flux.setState('account.data', undefined);
      void flux.setState('workspace.generationIntents', {});
    }
  }, [flux]));
  useFluxListener(AUTH_EVENTS.ERROR, useCallback((event?: AuthEvent) => {
    if(event?.operation === 'session') {
      setSessionError(true);
      setReady(true);
    }
  }, []));
  useEffect(() => {
    void auth.restoreSession();
  }, [auth]);
  useEffect(() => {
    let title = 'Make something worth watching';
    if(route === 'pricing') {
      title = 'Plans & pricing';
    } else if(route === 'features') {
      title = 'Features';
    } else if(route === 'privacy' || route === 'terms') {
      title = route === 'privacy' ? 'Privacy' : 'Terms of service';
    } else if((route === 'account' || authModes.includes(route || ''))) {
      title = 'Your account';
    } else if(workspaceRoutes.includes(route || '')) {
      title = 'Studio';
    }
    document.title = `${title} · Reactorbox`;
    window.scrollTo(0, 0);
  }, [location.key, route]);
  const signedIn = Boolean(user);
  const publicPage = (content: ReactElement, authPage = false): ReactElement =>
    <PublicShell authPage={authPage} signedIn={signedIn}>{content}</PublicShell>;
  const protectedContent = (content: ReactElement): ReactElement => {
    if(!ready) {
      return <div className="grid min-h-dvh place-items-center bg-black text-violet-200">Opening your studio…</div>;
    }
    if(sessionError) {
      return publicPage(<div className="mx-auto max-w-xl px-6 py-24 text-center"><h1 className="text-3xl text-violet-200">Let’s reconnect your account.</h1><p className="my-6 text-white/60">The account service is unavailable. Please try again.</p><button className="rounded-full bg-violet-600 px-6 py-3" onClick={() => void auth.restoreSession()} type="button">Try again</button></div>);
    }
    if(!user) {
      return <Navigate replace to="/sign-up" />;
    }
    return content;
  };
  return <Routes>
    <Route element={publicPage(<MarketingHome />)} path="/" />
    <Route element={<Navigate replace to="/" />} path="/home" />
    <Route element={publicPage(<FeaturesPage signedIn={signedIn} />)} path="/features" />
    <Route element={publicPage(<PricingPage signedIn={signedIn} />)} path="/pricing" />
    {(['privacy', 'terms'] as const).map((kind) => <Route element={publicPage(<LegalPage kind={kind} />)} key={kind} path={`/${kind}`} />)}
    {authModes.map((mode) => <Route element={publicPage(<AuthPage email={params.get('email') || undefined} key={`${location.pathname}${location.search}`} mode={mode as AuthMode} onSignedIn={() => void navigate('/projects')} token={params.get('token') || undefined} />, true)} key={mode} path={`/${mode}`} />)}
    <Route element={protectedContent(publicPage(user ? <AccountPage key={user.id} /> : <AuthPage mode="sign-in" onSignedIn={() => void navigate('/account', {replace: true})} />, !user))} path="/account" />
    {workspaceRoutes.map((view) => <Route element={protectedContent(<App key={user?.id || 'preview'} signedIn={signedIn} />)} key={view} path={`/${view}`} />)}
    <Route element={publicPage(<div className="mx-auto max-w-xl px-6 py-24 text-center"><h1 className="text-4xl text-violet-200">Page not found.</h1><p className="my-6 text-white/60">This page is no longer available.</p><Link className="text-sky-200" to="/">Back to Reactorbox</Link></div>)} path="*" />
  </Routes>;
};
